import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, User, LayoutDashboard, Menu, X, LogOut, LogIn, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const isActive = (path) => location.pathname === path;

  // Fetch unread messages count
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      // Poll every 30 seconds for new messages
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/chat/unread-count`);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.log("Could not fetch unread count");
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Te-ai deconectat cu succes");
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 glass-header">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/imofi_logo.png" 
              alt="Imofit" 
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden sm:flex items-center gap-1">
            <Link to="/">
              <Button
                variant="ghost"
                data-testid="nav-home"
                className={`rounded-full ${isActive("/") ? "bg-stone-100" : ""}`}
              >
                Anunțuri
              </Button>
            </Link>
            
            <Link to="/blog">
              <Button
                variant="ghost"
                data-testid="nav-blog"
                className={`rounded-full ${isActive("/blog") ? "bg-stone-100" : ""}`}
              >
                Blog
              </Button>
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link to="/dashboard">
                  <Button
                    variant="ghost"
                    data-testid="nav-dashboard"
                    className={`rounded-full relative ${isActive("/dashboard") ? "bg-stone-100" : ""}`}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Contul meu
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Button>
                </Link>
                
                {/* User info & Logout */}
                <div className="flex items-center gap-2 ml-2 pl-2 border-l border-stone-200">
                  <span className="text-sm text-stone-600 hidden md:inline">
                    {user?.name || user?.email}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="rounded-full text-stone-500 hover:text-red-600"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <Link to="/auth">
                <Button
                  variant="default"
                  data-testid="nav-login"
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Autentificare
                </Button>
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="sm:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-stone-700" />
            ) : (
              <Menu className="w-6 h-6 text-stone-700" />
            )}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="sm:hidden py-4 border-t border-stone-200 animate-fade-in">
            <div className="flex flex-col gap-2">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <Button
                  variant="ghost"
                  className={`w-full justify-start ${isActive("/") ? "bg-stone-100" : ""}`}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Anunțuri
                </Button>
              </Link>
              
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className={`w-full justify-start relative ${isActive("/dashboard") ? "bg-stone-100" : ""}`}
                    >
                      <User className="w-4 h-4 mr-2" />
                      Contul meu
                      {unreadCount > 0 && (
                        <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5 font-bold">
                          {unreadCount} mesaj{unreadCount > 1 ? "e" : ""} nou{unreadCount > 1 ? "ă" : ""}
                        </span>
                      )}
                    </Button>
                  </Link>
                  
                  {/* User & Logout */}
                  <div className="pt-2 mt-2 border-t border-stone-200">
                    <p className="px-4 text-sm text-stone-500 mb-2">
                      Conectat ca: {user?.name || user?.email}
                    </p>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-red-600 hover:bg-red-50"
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Deconectare
                    </Button>
                  </div>
                </>
              ) : (
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    className="w-full justify-start bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Autentificare
                  </Button>
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
